import React, { useEffect, useRef, useCallback } from 'react'
import { useTheme } from '@mui/material/styles'
import { useForm, useWatch } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import FormControl from '@mui/material/FormControl'
import InputAdornment from '@mui/material/InputAdornment'
import ErrorOutline from '@mui/icons-material/ErrorOutline'
import { useClinic } from '@/v2/hooks/useClinic'
import ControlRadioField from '@/v2/components/common/ControlRadioField'
import CurrencyInputField from '@/v2/components/common/CurrencyInputField'
import DiscountField from './components/DiscountField'
import InputField from '@/v2/components/common/InputField'
import InstallmentFields from './components/InstallmentFields'
import Typography from '@/v2/components/common/Typography'
import ActionButton from '@/v2/components/common/ActionButton'
import Row from '@/v2/components/common/Row'
import { updateContractAmounts } from '@/v2/utils/helpers'
import { CONTRACT_TREATMENT_TYPE_OPTIONS } from '@/v2/constants'

import { createContractSchema } from './schemas/createContractSchema'

const CreateContractForm = ({ state, setState, onClose }) => {
  const theme = useTheme()
  const errorContainerRef = useRef(null)
  const hydratedFromClinic = useRef(false)
  const { clinic: clinicSettings } = useClinic()

  const formDefaults = {
    classification: state.classification || 'one_time_payment',
    treatmentType: state.treatmentType || 'invisalign',
    description: state.description || '',
    amount: state.amount || '',
    downPayment: state.downPayment || '',
    discountRate: state.discountRate || 0,
    interestRate: state.interestRate || 0,
    processingFeePercentage: state.processingFeePercentage ?? '',
    installments: state.installments || 1,
    amountWithDiscount: 0,
    totalDue: 0,
    installmentAmount: 0,
    installmentAmountWithoutDiscount: 0,
  }

  const {
    register,
    handleSubmit,
    control,
    setValue: originalSetValue,
    reset,
    getFieldState,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = useForm({
    defaultValues: formDefaults,
    resolver: yupResolver(createContractSchema),
  })

  const setValue = useCallback(
    (name, value, options) => {
      return originalSetValue(name, value, options)
    },
    [originalSetValue]
  )

  // Seed the processing fee with the clinic default, but only when the form
  // started empty — a value carried back from the Confirm step (in `state`)
  // is the user's own and must not be overwritten on remount.
  useEffect(() => {
    if (hydratedFromClinic.current || !clinicSettings) return
    hydratedFromClinic.current = true
    const current = state.processingFeePercentage
    if (current === '' || current == null) {
      setValue(
        'processingFeePercentage',
        clinicSettings.processingFeePercentage ?? 0
      )
    }
  }, [clinicSettings, setValue, state.processingFeePercentage])

  const classification = useWatch({ control, name: 'classification' })
  const previousClassificationRef = useRef(classification)

  useEffect(() => {
    if (
      previousClassificationRef.current !== classification &&
      previousClassificationRef.current !== null
    ) {
      if (classification === 'one_time_payment') {
        setValue('downPayment', '', {
          shouldValidate: false,
          shouldDirty: false,
        })
        setValue('installments', 1, {
          shouldValidate: false,
          shouldDirty: false,
        })
        setValue('interestRate', 0, {
          shouldValidate: false,
          shouldDirty: false,
        })
      } else if (classification === 'installments') {
        setValue('installments', 2, {
          shouldValidate: false,
          shouldDirty: false,
        })
        setValue('downPayment', '', {
          shouldValidate: false,
          shouldDirty: false,
        })
      }
    }
    previousClassificationRef.current = classification
  }, [classification, setValue])

  const onSubmit = data => {
    const baseUpdate = { ...data, step: 2, error: null }

    if (data.classification === 'one_time_payment') {
      setState({ ...baseUpdate, ...updateContractAmounts(data) })
    } else {
      setState(baseUpdate)
    }
  }

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset()
    }
  }, [isSubmitSuccessful, reset])

  useEffect(() => {
    if (state.error) {
      errorContainerRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }
  }, [state.error])

  return (
    <FormControl
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      variant="filled"
    >
      {state.error && (
        <Box
          bgcolor={theme.palette.error.dark}
          color={theme.palette.common.white}
          p={1}
          display="flex"
          alignItems="center"
          gap={1}
          ref={errorContainerRef}
        >
          <ErrorOutline />
          <Typography>{state.error}</Typography>
        </Box>
      )}

      <ControlRadioField
        id="classification"
        control={control}
        options={[
          { label: 'Single', value: 'one_time_payment' },
          { label: 'Installments', value: 'installments' },
        ]}
        error={errors?.classification?.message}
        name="classification"
        color="secondary"
        property="value"
      />

      <ControlRadioField
        id="treatmentType"
        label="Treatment"
        control={control}
        options={CONTRACT_TREATMENT_TYPE_OPTIONS}
        error={errors?.treatmentType?.message}
        name="treatmentType"
        color="secondary"
        property="value"
      />

      <InputField
        fullWidth
        label="Description"
        name="description"
        autoFocus
        placeholder="Write a description here..."
        error={errors?.description?.message}
        {...register('description')}
      />

      <Row>
        <CurrencyInputField
          label="Amount"
          name="amount"
          placeholder="$0.00"
          prefix="$"
          error={errors?.amount?.message}
          control={control}
        />

        <DiscountField
          noMargin
          label="Apply discount"
          name="discountRate"
          id="payment-plan-discount"
          options={[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50].map(d => ({
            label: `${d}%`,
            value: d,
          }))}
          color="secondary"
          errors={errors}
          register={register}
          setValue={setValue}
          control={control}
          getFieldState={getFieldState}
          setState={setState}
        />
      </Row>

      <InstallmentFields
        errors={errors}
        register={register}
        setValue={setValue}
        control={control}
        getFieldState={getFieldState}
      />

      <InputField
        {...register('processingFeePercentage')}
        label="Processing Fee (Credit Card)"
        type="number"
        inputProps={{ step: '0.01', min: 0, max: 100 }}
        endAdornment={<InputAdornment position="end">%</InputAdornment>}
        error={errors?.processingFeePercentage?.message}
        fullWidth
      />

      <Grid container justifyContent="flex-end" gap={2}>
        <ActionButton
          variant="text"
          color="primary"
          onClick={onClose}
          disabled={isSubmitting}
        >
          Cancel
        </ActionButton>
        <ActionButton
          variant="contained"
          color="secondary"
          type="submit"
          loading={isSubmitting}
          disabled={isSubmitting}
        >
          Submit
        </ActionButton>
      </Grid>
    </FormControl>
  )
}

export default CreateContractForm
